import { useCallback, useEffect, useState } from 'react';

import { Card } from 'core/Api';
import { TokenEx, useBreakPoints, useFormListValidator, useTokenEx } from 'core/Hooks';
import { Validator } from 'core/Utils';
import { Alert, Box, Collapse, Stack } from 'ui';
import { hasLength } from 'ui/Form';

import classes from './CardInfo.module.css';

declare global {
  interface Window {
    TokenEx: any;
  }
}

type CardInfoFieldsProps = {
  withTerms?: boolean;
  error?: string;
  withCvv?: boolean;
};

export const CardInfoFields = ({
  withTerms = false,
  error,
  withCvv = true,
}: CardInfoFieldsProps) => {
  const [_error, setErrors] = useState<undefined | string>(error);
  const { isSmallScreen } = useBreakPoints();
  const {
    errorStates: cardErrorState,
    isConfigAndTokenReady,
    initiateTokenEx,
    isCardValid,
    tokenizeCard,
  } = useTokenEx({
    tokenizeOnValidate: false,
    useRemoteConfig: true,
    useCvv: withCvv,
    onAutoFill(autoFillValues) {
      const { nameOnCard: name, month, year } = TokenEx.formatAutoFill(autoFillValues);
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      form.setValues({
        name,
        month,
        year,
      });
    },
  });

  const form = TokenEx.useTokenExForm({
    initialValues: {
      name: '',
      month: '',
      year: '',
      zip: '',
      isCardValid: undefined,
      terms: false,
    },
    validate: {
      name: hasLength({ min: 2 }),
      terms: (value) => {
        if (withTerms) return value ? null : true;
        return null;
      },
      month: (value) => (/^(0[1-9])$|^(1[0-2])$/.test(value) ? null : true),
      year: hasLength({ min: 4 }),
      zip: (value) => (String(value).trim().length >= 5 ? null : true),
      isCardValid: (value) => (value ? null : cardErrorState.message),
    },
    validateInputOnBlur: true,
  });

  // We'll use this callback to format the card data and return it to the to the caller of the tokenization function
  const handleTokenize = useCallback(async () => {
    const cardForm = (await tokenizeCard()) as Card;
    form.validate();
    const { month, year, name, zip } = form.values;
    const isYearMonthValid = Validator.expirationDate({ month, year });
    if (isYearMonthValid.error) return setErrors(isYearMonthValid.error);
    if (cardForm) {
      const credential = {
        token: cardForm.token,
        hmac: cardForm.tokenHMAC,
        metadata: {
          card_type: cardForm.cardType,
          last_four: cardForm.lastFour,
          full_name: name,
          month: month,
          year: year,
          zip: zip,
        },
      };
      return credential;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values, tokenizeCard]);

  // Handle Card Validity and CardErrorStates
  useEffect(() => {
    if (isCardValid) {
      form.setFieldValue('isCardValid', true);
    } else {
      form.setFieldValue('isCardValid', undefined);
    }
    // otherwise, we'll set the form error state
    if (cardErrorState.isError) {
      form.setFieldError('isCardValid', cardErrorState.message);
    } else {
      form.clearFieldError('isCardValid');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardErrorState, isCardValid]);

  // Handle form.error changes
  useEffect(() => {
    // Check to see if there is an error on the form, if there is we'll decode it and set it to the error state
    const errorOne = Object.keys(form.errors)[0] as keyof typeof TokenEx.FormErrors;
    if (errorOne) {
      const errorMessage = errorOne == 'card' ? cardErrorState.message : undefined;
      setErrors(errorMessage ?? TokenEx.FormErrors[errorOne]);
    } else {
      setErrors(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only need to update the main error state when form.errors changes
  }, [form.errors]);

  useEffect(() => {
    isConfigAndTokenReady && initiateTokenEx('creditCard');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfigAndTokenReady]);

  // Set the global handler for the tokenization function
  TokenEx.useTokenize({ tokenizeCard: handleTokenize });
  useFormListValidator({ form, id: 'card_info' });

  /**
   * When given an outside error, update the local error state
   */
  useEffect(() => {
    if (error) {
      if (error != _error) {
        setErrors(error);
      }
    }
  }, [_error, error]);

  return (
    <TokenEx.Provider form={form}>
      <Box className={classes.form} component="form" autoComplete="on">
        <Stack gap={isSmallScreen ? 'md' : 'lg'}>
          <TokenEx.Fields withCvv={withCvv} />
          <Collapse in={!!_error}>
            <Alert type="error" title={_error}></Alert>
          </Collapse>
          <Stack gap="xs">
            <TokenEx.Disclaimer />
            {withTerms && <TokenEx.AgreeToTerms />}
          </Stack>
        </Stack>
      </Box>
    </TokenEx.Provider>
  );
};
